import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { CountryText } from '@/components/ui/CountryText'
import { StatusPill } from '@/components/ui/StatusPill'
import { useClientStatement } from '@/features/clients/api/useClientStatement'
import { ClientMoneyTiles } from '@/features/clients/components/ClientMoneyTiles'
import { ClientRecordActions } from '@/features/clients/components/ClientRecordActions'
import { ClientStatementTable } from '@/features/clients/components/ClientStatementTable'
import { useYearParam, YearControl } from '@/features/clients/components/YearControl'

/* One client: who they are, what they owe, and every shipment behind it for
 * the chosen year — the workbook's per-customer sheet with its opening line
 * and running balance. Money follows the server's `moneyUnavailable`
 * exactly as the overview does. A 404 covers both a missing client and one
 * outside the caller's scope, and ErrorState words it. */
export function ClientPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { clientId = '' } = useParams()
  const [year, setYear] = useYearParam()
  const { data, isPending, isError, error, refetch } = useClientStatement(clientId, year)

  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-72 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const { client } = data

  return (
    <>
      <PageHeader
        eyebrow={
          <span className="flex items-center gap-2">
            {t('clients.statement.eyebrow')}
            {client.code ? <span className="font-mono">{client.code}</span> : null}
          </span>
        }
        title={client.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <CountryText value={client.country} />
            {client.taxId ? <span>{t('directories.clients.taxId')} {client.taxId}</span> : null}
            <StatusPill tone={client.isActive ? 'positive' : 'neutral'}>
              {client.isActive ? t('common.form.active') : t('common.form.inactive')}
            </StatusPill>
          </span>
        }
        actions={
          <>
            <YearControl years={data.years} value={year} onChange={setYear} label={t('clients.year.label')} />
            <ClientRecordActions client={client} />
          </>
        }
      />

      {data.moneyUnavailable === 'unavailable' ? (
        <Banner tone="attention" title={t('clients.moneyUnavailable.title')}>
          {t('clients.moneyUnavailable.body')}
        </Banner>
      ) : null}

      {data.totals && data.opening ? (
        <ClientMoneyTiles money={data.totals} opening={data.opening.balance} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('clients.statement.title')}</CardTitle>
        </CardHeader>
        <ClientStatementTable
          rows={data.rows}
          opening={data.opening?.balance}
          totals={data.totals}
          onOpenCase={(row) => navigate(`/cases/${row.caseId}`)}
        />
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('clients.statement.details')}</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] md:grid-cols-2">
            <dt className="text-text-muted">{t('directories.clients.registrationId')}</dt>
            <dd>{client.registrationId || t('common.empty')}</dd>
            <dt className="text-text-muted">{t('directories.clients.address')}</dt>
            <dd>{client.address || t('common.empty')}</dd>
          </dl>
          {client.contacts.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-1.5 text-[13px]">
              {client.contacts.map((contact) => (
                <li key={contact.id} className="flex flex-wrap gap-x-4">
                  <span className="text-text-heading">{contact.label}</span>
                  <SuppressedValue value={contact.email} />
                  <SuppressedValue value={contact.phone} />
                </li>
              ))}
            </ul>
          ) : null}
        </CardBody>
      </Card>
    </>
  )
}
